import {
    ActionResponse,
    ActionStatusHelper,
    DomainTableConfig,
    useAppEnum,
    useLayoutLifeCycle,
} from '@/Shared';

import { LeaseHeader } from '../lease-config';
import { useLeaseEnricher } from '../Hooks';
import { DOMAIN_CONFIGS, DomainHookProps } from '@/Shared/Config/domain-config';
import { useDocument, useLease } from '@/OneLiners';
import { LeaseViewModel } from '../ViewModels/LeaseViewModel';
import { LeaseModel } from '../LeaseModel';

/**
 * useLeaseTableConfig hook
 *
 * Domain-specific layout composition hook for Lease entities.
 * Orchestrates data, enrichment, permissions, and UI table configuration.
 *
 * Responsibilities:
 * - Aggregate Lease domain state, enrichment, and permissions into a single response.
 * - Provide dynamic headers for Lease table.
 * - Map strict Model → ViewModel for UI consumption.
 * - Delegate lifecycle orchestration to useLayoutLifeCycle.
 *
 * @date: March 2, 2026
 * @author: Samano CASTRE
 * 
 * @see DomainHookProps
 * @see DomainTableConfig<LeaseViewModel>
 *
 * @param props Domain-level configuration and permissions
 * @returns DomainTableConfig<LeaseViewModel>
 */
export function useLeaseTableConfig(props: DomainHookProps): DomainTableConfig<LeaseViewModel> {
    const { permissionHook, accessRight } = props;
    const { domainType } = DOMAIN_CONFIGS.Lease;

    /**
     * Domain enums for headers and VM mapping.
     */
    const { LeaseEnums } = useAppEnum();

    /**
     * Lease domain entry point (CRUD and state).
     */
    const leaseHook = useLease();

    /**
     * Document domain state (related documents for Lease).
     */
    const {
        dataMap: documentMap,
        stateResponse: documentResponse,
    } = useDocument();

    /**
     * Enrichment layer
     *
     * Responsible for resolving user-related data and
     * computing derived Lease fields.
     */
    const {
        userMap,
        getStrictEnrichedLease,
        enricherResponse,
    } = useLeaseEnricher();

    /**
     * Combined readiness response.
     *
     * The layout lifecycle initializes only once this
     * contract reaches a succeeded state.
     */
    const response: ActionResponse = {
        status: ActionStatusHelper.combine(
            leaseHook.stateResponse.status,
            documentResponse.status,
            enricherResponse.status,
            permissionHook.stateResponse.status
        ),
        error: leaseHook.stateResponse.error ??
            documentResponse.error ??
            enricherResponse.error
    };

    /**
    * Delegation to generic layout lifecycle.
    */
    const lifeCycleHook = useLayoutLifeCycle({
        response,
        domainType,
        props,
        entityHook: leaseHook,

        /**
         * Header configuration for Property table.
         */
        headers: LeaseHeader.build(LeaseEnums),

        /**
         * Strict Model → ViewModel transformation.
         */
        toViewModelFn: (
            lease: LeaseModel, isDisplaying: boolean) =>
            new LeaseViewModel({
                lease: getStrictEnrichedLease(lease),
                viewEnums: LeaseEnums,
                documentMap,
                accessRight,
                isDisplaying
            })
    });

    return lifeCycleHook;
}